/**
 * SignGestionPage - Signature electronique publique des mandats de gestion locative.
 * Accessible via /sign-gestion/<token> sans authentification.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignGestionPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [fullName, setFullName] = useState('');
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gestion/public/${token}`)
      .then(r => {
        setData(r.data);
        setFullName(r.data?.owner?.name || '');
        if (r.data?.already_signed) setSuccess({ number: r.data.mandate.number });
      })
      .catch(e => {
        if (!e.response) setError('Connexion au serveur impossible. Vérifiez votre connexion internet.');
        else if (e.response.status === 404) setError('Mandat introuvable. Le lien est peut-être incorrect.');
        else setError(e.response?.data?.detail || 'Erreur inattendue.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    let drawing = false;
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      return { x: x * scaleX, y: y * scaleY };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing = false; };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [data, success]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSign = async () => {
    if (!hasDrawn) { alert('Merci de dessiner votre signature.'); return; }
    if (!fullName.trim() || fullName.trim().length < 3) { alert('Merci de saisir votre nom complet.'); return; }
    if (!accept) { alert('Vous devez accepter les termes du mandat.'); return; }
    const signaturePng = canvasRef.current.toDataURL('image/png');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/gestion/public/${token}/sign`, {
        signature_png_base64: signaturePng,
        full_name: fullName.trim(),
        accept_terms: true,
      });
      setSuccess(r.data);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="text-gray-500">Chargement du mandat...</div></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="sign-gestion-success">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-10 text-center">
        <svg className="w-20 h-20 mx-auto mb-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Document signé avec succès !</h1>
        <p className="text-gray-600 mb-6">Votre document <b>{success.number || data?.mandate?.number}</b> a été signé électroniquement. Un email de confirmation vous a été envoyé avec le PDF signé.</p>
        <a href={`${API}/gestion/public/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-block bg-[#008A8D] text-white font-semibold py-3 px-8 rounded-lg hover:bg-[#006B6D]" data-testid="download-signed-mandate-pdf">
          Télécharger le PDF signé
        </a>
      </div>
    </div>
  );

  const m = data.mandate;
  const typeLbl = m.type === 'gestion' ? 'Mandat de Gestion Locative'
    : m.type === 'mise_en_location' ? 'Mandat de Mise en Location'
    : m.type === 'bail_meuble' ? 'Bail de Location Meublée'
    : m.type === 'bail_nu' ? 'Bail de Location Nue'
    : 'Document à signer';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="sign-gestion-page">
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#008A8D] text-white p-6 rounded-t-xl">
          <h1 className="text-2xl font-bold">{typeLbl} {m.number}</h1>
          <p className="text-white/80 text-sm mt-1">{data.owner?.name} — {data.address} | RealEase</p>
        </div>
        <div className="bg-white border-x border-gray-200 p-0">
          <iframe srcDoc={data.html} className="w-full" style={{ height: '700px', border: 'none' }}
            title="Mandat" sandbox="" data-testid="mandate-preview" />
        </div>
        <div className="bg-white border border-gray-200 rounded-b-xl p-6 space-y-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Signature électronique</h2>
            <p className="text-sm text-gray-500">Dessinez votre signature dans la zone ci-dessous (souris ou doigt sur mobile).</p>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 relative">
            <canvas ref={canvasRef} width={800} height={200} className="w-full bg-white cursor-crosshair touch-none"
              style={{ maxHeight: '200px' }} data-testid="gestion-signature-canvas" />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-gray-300 text-sm italic">Dessinez votre signature ici</span>
              </div>
            )}
          </div>
          <button onClick={clearCanvas} className="text-sm text-gray-500 hover:text-[#008A8D]" data-testid="clear-gestion-signature-btn">Effacer et recommencer</button>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Votre nom complet *</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
              placeholder="Prénom NOM" data-testid="gestion-sign-full-name" required />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#008A8D]" data-testid="gestion-sign-accept-terms" />
            <span className="text-sm text-gray-700 leading-relaxed">
              Je reconnais avoir lu et compris les termes du document ci-dessus, et je m'engage à le respecter.
              J'accepte que ma signature électronique ait la même valeur juridique qu'une signature manuscrite.
            </span>
          </label>
          <button onClick={handleSign}
            disabled={submitting || !hasDrawn || !accept || !fullName.trim()}
            className="w-full bg-[#008A8D] text-white font-semibold py-4 rounded-lg hover:bg-[#006B6D] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg"
            data-testid="submit-gestion-signature-btn">
            {submitting ? 'Signature en cours...' : 'Signer le mandat'}
          </button>
          <p className="text-xs text-gray-400 text-center">
            Votre signature, votre IP et la date seront enregistrées comme preuve. RealEase | <a href="mailto:contact@real-ease.fr" className="underline">contact@real-ease.fr</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignGestionPage;
